import { useState } from 'react';
import {
  Settings, GitBranch, Tag, Upload, Users, Sliders,
  Plus, GripVertical, Trash2, Edit3, Save, X, Loader2,
} from 'lucide-react';
import { usePipelines, useCreatePipeline, type Pipeline } from '../hooks/usePipelines';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fetchJson, postJson, putJson, deleteJson } from '../lib/api';
import { Badge } from '../components/shared/Badge';
import { cn } from '../lib/cn';

interface SettingsViewProps {
  onNavigate: (view: string, params?: Record<string, string>) => void;
}

type SettingsTab = 'general' | 'pipelines' | 'custom-fields' | 'tags' | 'import' | 'team';

const tabs: { id: SettingsTab; label: string; icon: typeof Settings }[] = [
  { id: 'general', label: 'General', icon: Sliders },
  { id: 'pipelines', label: 'Pipelines', icon: GitBranch },
  { id: 'custom-fields', label: 'Custom Fields', icon: Edit3 },
  { id: 'tags', label: 'Tags', icon: Tag },
  { id: 'import', label: 'Import', icon: Upload },
  { id: 'team', label: 'Team', icon: Users },
];

interface CustomField {
  id: string;
  name: string;
  entityType: string;
  fieldType: string;
  required: boolean;
  options?: string[];
}

interface TeamMember {
  id: string;
  email: string;
  name: string;
  role: string;
  status: string;
}

export function SettingsView({ onNavigate }: SettingsViewProps) {
  const [activeTab, setActiveTab] = useState<SettingsTab>('general');

  return (
    <div className="flex-1 overflow-y-auto">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="text-xl font-bold text-slate-900">Settings</h1>
          <p className="mt-0.5 text-sm text-slate-500">Configure your CRM preferences</p>
        </div>
      </div>

      <div className="flex">
        {/* Tab Navigation */}
        <nav className="w-48 shrink-0 border-r border-surface-200 bg-white p-4">
          <ul className="space-y-1">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <li key={tab.id}>
                  <button
                    onClick={() => setActiveTab(tab.id)}
                    className={cn(
                      'flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition-colors',
                      activeTab === tab.id
                        ? 'bg-brand-50 text-brand-700'
                        : 'text-slate-600 hover:bg-surface-50 hover:text-slate-900',
                    )}
                  >
                    <Icon className="h-4 w-4" />
                    {tab.label}
                  </button>
                </li>
              );
            })}
          </ul>
        </nav>

        {/* Tab Content */}
        <div className="flex-1 p-6">
          {activeTab === 'general' && <GeneralSettings />}
          {activeTab === 'pipelines' && <PipelineSettings />}
          {activeTab === 'custom-fields' && <CustomFieldSettings />}
          {activeTab === 'tags' && <TagSettings />}
          {activeTab === 'import' && <ImportSettings />}
          {activeTab === 'team' && <TeamSettings />}
        </div>
      </div>
    </div>
  );
}

/* ========= General ========= */
function GeneralSettings() {
  const [defaultPipeline, setDefaultPipeline] = useState('');
  const [timezone, setTimezone] = useState(Intl.DateTimeFormat().resolvedOptions().timeZone);

  return (
    <div className="max-w-lg space-y-6">
      <h2 className="text-lg font-semibold text-slate-800">General Preferences</h2>

      <div>
        <label className="label mb-1">Default Pipeline</label>
        <select
          className="input w-full"
          value={defaultPipeline}
          onChange={(e) => setDefaultPipeline(e.target.value)}
        >
          <option value="">Select default pipeline...</option>
        </select>
        <p className="mt-1 text-xs text-slate-400">
          The pipeline shown by default when opening the Deals view.
        </p>
      </div>

      <div>
        <label className="label mb-1">Timezone</label>
        <select
          className="input w-full"
          value={timezone}
          onChange={(e) => setTimezone(e.target.value)}
        >
          <option value="America/New_York">Eastern (ET)</option>
          <option value="America/Chicago">Central (CT)</option>
          <option value="America/Denver">Mountain (MT)</option>
          <option value="America/Los_Angeles">Pacific (PT)</option>
          <option value="UTC">UTC</option>
        </select>
      </div>

      <button className="btn-primary">
        <Save className="h-4 w-4" />
        Save Preferences
      </button>
    </div>
  );
}

/* ========= Pipelines ========= */
function PipelineSettings() {
  const { data: pipelines, isLoading } = usePipelines();
  const createPipeline = useCreatePipeline();
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState('');
  const [newStages, setNewStages] = useState<{ name: string; color: string }[]>([
    { name: 'New', color: '#3B82F6' },
    { name: 'Qualified', color: '#8B5CF6' },
    { name: 'Proposal', color: '#F59E0B' },
    { name: 'Negotiation', color: '#EF4444' },
    { name: 'Closed Won', color: '#10B981' },
  ]);

  function handleCreate() {
    if (!newName.trim()) return;
    createPipeline.mutate(
      { name: newName.trim(), stages: newStages },
      {
        onSuccess: () => {
          setShowCreate(false);
          setNewName('');
        },
      },
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-slate-800">Pipelines</h2>
        <button className="btn-primary btn-sm" onClick={() => setShowCreate(!showCreate)}>
          <Plus className="h-3.5 w-3.5" />
          New Pipeline
        </button>
      </div>

      {showCreate && (
        <div className="card p-4 space-y-4">
          <div>
            <label className="label mb-1">Pipeline Name</label>
            <input
              type="text"
              className="input w-full"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="e.g., Commercial Lines"
            />
          </div>
          <div>
            <label className="label mb-1">Stages</label>
            <div className="space-y-2">
              {newStages.map((stage, i) => (
                <div key={i} className="flex items-center gap-2">
                  <GripVertical className="h-4 w-4 text-slate-300" />
                  <input
                    type="color"
                    value={stage.color}
                    onChange={(e) => {
                      const updated = [...newStages];
                      updated[i] = { ...updated[i], color: e.target.value };
                      setNewStages(updated);
                    }}
                    className="h-8 w-8 rounded border border-surface-200 cursor-pointer"
                  />
                  <input
                    type="text"
                    className="input flex-1"
                    value={stage.name}
                    onChange={(e) => {
                      const updated = [...newStages];
                      updated[i] = { ...updated[i], name: e.target.value };
                      setNewStages(updated);
                    }}
                  />
                  <button
                    onClick={() => setNewStages(newStages.filter((_, idx) => idx !== i))}
                    className="btn-ghost p-1 text-red-400 hover:text-red-600"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
            </div>
            <button
              className="btn-secondary btn-sm mt-2"
              onClick={() => setNewStages([...newStages, { name: '', color: '#6B7280' }])}
            >
              <Plus className="h-3.5 w-3.5" />
              Add Stage
            </button>
          </div>
          <div className="flex justify-end gap-2">
            <button className="btn-secondary" onClick={() => setShowCreate(false)}>Cancel</button>
            <button className="btn-primary" onClick={handleCreate} disabled={createPipeline.isPending}>
              {createPipeline.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              Create Pipeline
            </button>
          </div>
        </div>
      )}

      {isLoading ? (
        <div className="space-y-3">
          {Array.from({ length: 2 }).map((_, i) => (
            <div key={i} className="h-24 animate-pulse rounded-lg bg-surface-200" />
          ))}
        </div>
      ) : pipelines && pipelines.length > 0 ? (
        <div className="space-y-3">
          {pipelines.map((pipeline) => (
            <div key={pipeline.id} className="card p-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-semibold text-slate-700">{pipeline.name}</h3>
                  {pipeline.isDefault && <Badge color="blue" size="sm">Default</Badge>}
                </div>
                <button className="btn-secondary btn-sm">
                  <Edit3 className="h-3.5 w-3.5" />
                  Edit
                </button>
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                {pipeline.stages.map((stage) => (
                  <div key={stage.id} className="flex items-center gap-1.5 rounded-full border border-surface-200 px-2.5 py-1 text-xs text-slate-600">
                    {stage.color && (
                      <div className="h-2 w-2 rounded-full" style={{ backgroundColor: stage.color }} />
                    )}
                    {stage.name}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="card p-8 text-center text-sm text-slate-400">
          No pipelines configured yet.
        </div>
      )}
    </div>
  );
}

/* ========= Custom Fields ========= */
function CustomFieldSettings() {
  const { data, isLoading } = useQuery({
    queryKey: ['custom-fields'],
    queryFn: () => fetchJson<{ fields: CustomField[] }>('/api/settings/custom-fields'),
  });

  const fields = data?.fields ?? [];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-slate-800">Custom Fields</h2>
        <button className="btn-primary btn-sm">
          <Plus className="h-3.5 w-3.5" />
          Add Field
        </button>
      </div>

      {isLoading ? (
        <div className="h-48 animate-pulse rounded-lg bg-surface-200" />
      ) : fields.length > 0 ? (
        <div className="card overflow-hidden">
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-header">Name</th>
                <th className="table-header">Entity Type</th>
                <th className="table-header">Field Type</th>
                <th className="table-header">Required</th>
                <th className="table-header w-16"></th>
              </tr>
            </thead>
            <tbody>
              {fields.map((field) => (
                <tr key={field.id} className="table-row-hover">
                  <td className="table-cell font-medium text-slate-700">{field.name}</td>
                  <td className="table-cell"><Badge color="gray">{field.entityType}</Badge></td>
                  <td className="table-cell text-sm text-slate-600">{field.fieldType}</td>
                  <td className="table-cell">
                    {field.required ? (
                      <Badge color="orange" size="sm">Required</Badge>
                    ) : (
                      <span className="text-slate-400">Optional</span>
                    )}
                  </td>
                  <td className="table-cell">
                    <button className="btn-ghost btn-sm p-1"><Edit3 className="h-3.5 w-3.5" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="card p-8 text-center text-sm text-slate-400">
          No custom fields defined. Add fields to track additional data on contacts, organizations, or deals.
        </div>
      )}
    </div>
  );
}

/* ========= Tags ========= */
function TagSettings() {
  const { data, isLoading } = useQuery({
    queryKey: ['tags-settings'],
    queryFn: () => fetchJson<{ tags: { id: string; name: string; color: string; category?: string; usageCount: number }[] }>('/api/tags'),
  });

  const tags = data?.tags ?? [];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-slate-800">Tags</h2>
        <button className="btn-primary btn-sm">
          <Plus className="h-3.5 w-3.5" />
          Add Tag
        </button>
      </div>

      {isLoading ? (
        <div className="h-48 animate-pulse rounded-lg bg-surface-200" />
      ) : tags.length > 0 ? (
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
          {tags.map((tag) => (
            <div key={tag.id} className="card flex items-center justify-between p-3">
              <div className="flex items-center gap-2">
                <div className="h-3 w-3 rounded-full" style={{ backgroundColor: tag.color }} />
                <span className="text-sm font-medium text-slate-700">{tag.name}</span>
                {tag.category && (
                  <Badge color="gray" size="sm">{tag.category}</Badge>
                )}
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-slate-400">{tag.usageCount} uses</span>
                <button className="btn-ghost btn-sm p-1"><Edit3 className="h-3 w-3" /></button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="card p-8 text-center text-sm text-slate-400">
          No tags created yet.
        </div>
      )}
    </div>
  );
}

/* ========= Import ========= */
function ImportSettings() {
  const [defaultSource, setDefaultSource] = useState('');

  return (
    <div className="max-w-lg space-y-6">
      <h2 className="text-lg font-semibold text-slate-800">Import Settings</h2>

      <div>
        <label className="label mb-1">Default Source Folder</label>
        <input
          type="text"
          className="input w-full"
          value={defaultSource}
          onChange={(e) => setDefaultSource(e.target.value)}
          placeholder="s3://bucket/outlook-exports/"
        />
        <p className="mt-1 text-xs text-slate-400">
          S3 path to automatically scan for new import files.
        </p>
      </div>

      <button className="btn-primary">
        <Save className="h-4 w-4" />
        Save Settings
      </button>
    </div>
  );
}

/* ========= Team ========= */
function TeamSettings() {
  const { data, isLoading } = useQuery({
    queryKey: ['team-members'],
    queryFn: () => fetchJson<{ members: TeamMember[] }>('/api/settings/team'),
  });

  const members = data?.members ?? [];

  return (
    <div className="space-y-6">
      <h2 className="text-lg font-semibold text-slate-800">Team Members</h2>

      {isLoading ? (
        <div className="h-48 animate-pulse rounded-lg bg-surface-200" />
      ) : members.length > 0 ? (
        <div className="card overflow-hidden">
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-header">Name</th>
                <th className="table-header">Email</th>
                <th className="table-header">Role</th>
                <th className="table-header">Status</th>
              </tr>
            </thead>
            <tbody>
              {members.map((member) => (
                <tr key={member.id} className="table-row-hover">
                  <td className="table-cell font-medium text-slate-700">{member.name}</td>
                  <td className="table-cell text-sm text-slate-600">{member.email}</td>
                  <td className="table-cell"><Badge color="blue">{member.role}</Badge></td>
                  <td className="table-cell">
                    <Badge color={member.status === 'active' ? 'green' : 'gray'}>
                      {member.status}
                    </Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="card p-8 text-center text-sm text-slate-400">
          Team members will appear here based on shared user preferences.
        </div>
      )}
    </div>
  );
}
